<template>
    <div v-if="loading" class="flex flex-col gap-3">
        <MiscSkeleton :repeat="4" />
    </div>
    <template v-else>
        <div class="font-semibold">
            {{ computedTitle }}
        </div>
        <div class="text-sm">
            <div>
                {{ computedText }}
            </div>
            <button 
                v-if="showToggle" 
                type="button" 
                class="btn btn-ghost btn-sm flex items-center text-sm float-end" 
                @click="toggle()"
            >
                <span v-if="!showText">{{ $t('product.detail.reviews.review.text.toggleMore') }}</span>
                <span v-else>{{ $t('product.detail.reviews.review.text.toggleLess') }}</span>
                <MiscIcon :name="showText ? 'chevron-up' : 'chevron-down'" size="sm" class="pt-px" />
            </button>
        </div>
        <div v-if="computedComment != null" class="text-base-content-subtle text-sm mt-3">
            <span>— {{ $t('product.detail.reviews.review.commentLabel') }}: </span>{{ computedComment }}
        </div>
        <template v-if="review.status">
            <button
                v-if="languageIdChain !== review.languageId && !showTranslation"
                type="button"
                class="btn btn-link mr-auto p-0"
                @click="translate()"
            >
                {{ $t('product.detail.reviews.review.translate', { contextLanguage: $t('product.detail.reviews.review.target-'+getLanguageNameById(languageIdChain)) }) }}
            </button>
            <template v-if="showTranslation">
                <div class="text-sm font-bold mt-3">
                    {{ $t('product.detail.reviews.review.translatedFrom', { reviewLanguage: $t('product.detail.reviews.review.from-'+getLanguageNameById(review.languageId))}) }}
                </div>
                <button type="button" class="btn btn-link mr-auto p-0" @click="showTranslation = false">
                    {{ $t('product.detail.reviews.review.showOriginal') }}
                </button>
            </template>
        </template>
    </template>
</template>

<script setup lang="ts">
import type { Schemas } from '#shopware'

const props = withDefaults(defineProps<{
    review: Schemas['ProductReview']
    limit?: number
}>(), {
    limit: 200
})

const { apiClient } = useShopwareContext()
const { languageIdChain } = useSessionContext()
const config = useRuntimeConfig().public

const showToggle = ref(props.review.content.length > props.limit)
const showText = ref(props.review.content.length <= props.limit)
const showTranslation = ref(false)
const translatedTitle = ref()
const translatedText = ref()
const translatedComment = ref()
const loading = ref(false)
const nuxtApp = useNuxtApp()

const computedTitle = computed(() => {
    return showTranslation.value && translatedTitle.value != null ? translatedTitle.value : props.review.title
})

const computedText = computed(() => {
    const text = showTranslation.value && translatedText.value != null ? translatedText.value : props.review.content

    if (!showText.value) {
        return text.substring(0, props.limit) + '...'
    }

    return text
})

const computedComment = computed(() => {
    return showTranslation.value && translatedComment.value != null ? translatedComment.value : props.review.comment
})

function toggle () {
    showText.value = !showText.value
}

function getLanguageNameById (id: string) {
    const language = config?.platformLanguages?.find((language) => language.id === id)

    if (language) {
        return language.name
    }
}

async function translate () {
    showTranslation.value = true

    if (translatedTitle.value == null && translatedText.value == null) {
        loading.value = true

        const locale = nuxtApp.vueApp.config.globalProperties.$i18n?.locale;
        let languageId = null
        if (config.platformLanguages != null && config.platformLanguages?.length > 0 && locale) {
            const matchingLang = config.platformLanguages.find((lang) => {
                return lang.route === locale;
            });

            if (matchingLang) {
                languageId = matchingLang.id;
            }
        }

        try {
            const { data } = await apiClient.invoke('translateReview post /translate-review', {
                body: {
                    reviewId: props.review.id,
                    ...(languageId && { languageId })
                }
            })

            if (data != null) {
                translatedTitle.value = data.title
                translatedText.value = data.content
                translatedComment.value = data.comment
            }
        } catch(e) {
            console.error(e)
        }
        loading.value = false
    }
}
</script>
